import React, { useId, useLayoutEffect, useMemo, useRef } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import type { DocumentLink } from "@/models/document-link/DocumentLink";
import { useReferenceSidebarHoverPreviewStore } from "@/stores/reference-sidebar-hover-preview.store";
import { useReferencesSidebarFiltersStore } from "@/stores/references-sidebar-filters.store";
import { getEffectiveDivFields } from "@/partials/document-renderer/utils/documentLinkDivUtils";
import {
  getOtherSideLegislationTitleForTooltip,
  getOtherSideReferenceLabel,
  getOtherSideWorkspaceName,
  isOtherSideHistorical,
  isOtherSideWorkspaceLink,
} from "@/utils/documentLinkOtherSide";
import {
  buildWorkspaceReferenceVisibilityContext,
  collectDocumentWorkspaceReferenceLinks,
  filterWorkspaceReferenceLinksForDisplay,
  type WorkspaceReferenceVisibilityContext,
} from "@/utils/documentLinkWorkspaceVersionCollapse";
import {
  docLinkGroupIdFromTriggerId,
  hasVisibleDocumentLinkText,
} from "@/partials/document-renderer/utils/documentLinkInjectionPlan";
import { NEW_TAB_MODIFIER_TOOLTIP } from "@/lib/navigation/appNavigation";

interface MultiLinkAnchorProps {
  allDocumentLinks?: DocumentLink[];
  workspaceVisibilityContext?: WorkspaceReferenceVisibilityContext;
  links: Array<{
    link: DocumentLink;
    linkId: string;
    anchorId: string;
  }>;
  matchText: string;
  /** When set (e.g. split segments for one merged reference), used as the trigger <a id>. */
  triggerId?: string;
  /** Open document id — used for labels and must match navigation in useDocumentLinks */
  currentDocumentId?: string;
  navigateToDocumentLink: (
    linkId: string,
    event?: React.MouseEvent | MouseEvent
  ) => void;
}

export const MultiLinkAnchor: React.FC<MultiLinkAnchorProps> = ({
  allDocumentLinks,
  workspaceVisibilityContext: workspaceVisibilityContextProp,
  links,
  matchText,
  triggerId,
  currentDocumentId,
  navigateToDocumentLink,
}) => {
  const showHistorical = useReferencesSidebarFiltersStore((s) => s.showHistorical);
  const showEarlierWorkspaceVersions = useReferencesSidebarFiltersStore(
    (s) => s.showEarlierWorkspaceVersions
  );
  const setShowEarlierWorkspaceVersions = useReferencesSidebarFiltersStore(
    (s) => s.setShowEarlierWorkspaceVersions
  );
  const versionToggleId = useId();
  const hoverLeaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const beginHoverPreview = useReferenceSidebarHoverPreviewStore(
    (s) => s.beginHover
  );
  const endHoverPreview = useReferenceSidebarHoverPreviewStore((s) => s.endHover);
  const beginContentHover = useReferenceSidebarHoverPreviewStore(
    (s) => s.beginContentHover
  );
  const endContentHover = useReferenceSidebarHoverPreviewStore(
    (s) => s.endContentHover
  );

  const contentHoverLeaveRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const linkIdsForContentHover = useMemo(
    () => links.map((l) => l.linkId),
    [links]
  );

  const docLinkGroupId = useMemo(
    () => docLinkGroupIdFromTriggerId(triggerId),
    [triggerId]
  );

  /* Radix DropdownMenuTrigger injects type="button" (meant for <button>); on <a> it is invalid
     and breaks inline wrapping. Strip after merge — menu trigger semantics stay via aria-* / Radix. */
  const anchorDomId = triggerId ?? links[0]?.anchorId;
  useLayoutEffect(() => {
    if (!anchorDomId) return;
    document.getElementById(anchorDomId)?.removeAttribute("type");
  }, [anchorDomId]);

  /* Inter-block whitespace-only / invisible-only segments must not render an empty <a>. */
  if (!hasVisibleDocumentLinkText(matchText)) {
    return null;
  }

  const clearContentHoverLeave = () => {
    if (contentHoverLeaveRef.current) {
      clearTimeout(contentHoverLeaveRef.current);
      contentHoverLeaveRef.current = null;
    }
  };

  const scheduleEndContentHover = () => {
    clearContentHoverLeave();
    contentHoverLeaveRef.current = setTimeout(() => {
      endContentHover();
      contentHoverLeaveRef.current = null;
    }, 280);
  };

  const clearHoverLeaveTimeout = () => {
    if (hoverLeaveTimeoutRef.current) {
      clearTimeout(hoverLeaveTimeoutRef.current);
      hoverLeaveTimeoutRef.current = null;
    }
  };

  /** Sidebar: only this link while pointer is on a specific menu row. */
  const handleSidebarItemPointerEnter = (linkId: string) => {
    clearContentHoverLeave();
    beginContentHover([linkId], docLinkGroupId ?? null);
  };

  const handleItemPointerEnter = (link: DocumentLink) => {
    clearHoverLeaveTimeout();
    if (!currentDocumentId) return;
    const eff = getEffectiveDivFields(link, currentDocumentId);
    beginHoverPreview(
      currentDocumentId,
      eff.uniqueMatchText,
      eff.uniqueChunkText,
      eff.htmlIds,
      { scrollToMatch: false }
    );
  };

  const handleItemPointerLeave = () => {
    hoverLeaveTimeoutRef.current = setTimeout(() => {
      endHoverPreview();
      hoverLeaveTimeoutRef.current = null;
    }, 280);
  };

  const handleDropdownOpenChange = (open: boolean) => {
    if (!open) {
      clearHoverLeaveTimeout();
      endHoverPreview();
    }
  };

  const linkModels = links.map((l) => l.link);

  const poolAfterHistorical =
    !currentDocumentId || showHistorical
      ? linkModels
      : linkModels.filter(
          (l) => !isOtherSideHistorical(l, currentDocumentId)
        );

  const workspaceVisibilityContext =
    workspaceVisibilityContextProp ??
    (currentDocumentId &&
    allDocumentLinks &&
    !showEarlierWorkspaceVersions &&
    poolAfterHistorical.some((l) =>
      isOtherSideWorkspaceLink(l, currentDocumentId)
    )
      ? buildWorkspaceReferenceVisibilityContext(
          collectDocumentWorkspaceReferenceLinks(
            allDocumentLinks,
            currentDocumentId
          ),
          currentDocumentId
        )
      : undefined);

  const displayPool =
    !currentDocumentId
      ? poolAfterHistorical
      : filterWorkspaceReferenceLinksForDisplay(
          poolAfterHistorical,
          currentDocumentId,
          showEarlierWorkspaceVersions,
          workspaceVisibilityContext
        );

  const showVersionToggle =
    Boolean(currentDocumentId) &&
    poolAfterHistorical.length > 0 &&
    displayPool.length < poolAfterHistorical.length;

  const filteredLatestAfterPool = displayPool;

  const visibleIds = new Set(filteredLatestAfterPool.map((l) => l.id));

  const displayRows = links
    .filter((row) => visibleIds.has(row.link.id))
    .sort((a, b) =>
      getOtherSideReferenceLabel(a.link, currentDocumentId).localeCompare(
        getOtherSideReferenceLabel(b.link, currentDocumentId),
        "da"
      )
    );

  const handleItemClick = (
    linkId: string,
    event?: React.MouseEvent | MouseEvent
  ) => {
    navigateToDocumentLink(linkId, event);
  };

  if (currentDocumentId && displayPool.length === 0) {
    return <span className="doc-link-plain">{matchText}</span>;
  }

  return (
    <DropdownMenu onOpenChange={handleDropdownOpenChange}>
      <DropdownMenuTrigger asChild>
        <a
          id={triggerId ?? displayRows[0]?.anchorId}
          href="#"
          rel="noopener noreferrer nofollow"
          title={
            links.length > 1
              ? `Flere henvisninger — vælg måldokument. ${NEW_TAB_MODIFIER_TOOLTIP}`
              : "Henvisning — vælg måldokument"
          }
          data-document-link-ids={linkIdsForContentHover.join(",")}
          {...(docLinkGroupId
            ? { "data-doc-link-group": docLinkGroupId }
            : {})}
          onPointerEnter={() => {
            clearContentHoverLeave();
            beginContentHover(linkIdsForContentHover, docLinkGroupId ?? null);
          }}
          onPointerLeave={scheduleEndContentHover}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          className="doc-link-anchor"
          type={undefined}
        >
          {matchText}
        </a>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="max-w-sm"
        align="start"
        onPointerEnter={() => {
          clearContentHoverLeave();
          beginContentHover(linkIdsForContentHover, docLinkGroupId ?? null);
        }}
        onPointerLeave={scheduleEndContentHover}
      >
        {showVersionToggle ? (
          <div
            className="flex items-center gap-2 border-b border-border px-2 py-2"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onPointerEnter={() => {
              clearHoverLeaveTimeout();
              endHoverPreview();
              clearContentHoverLeave();
              endContentHover();
            }}
          >
            <Switch
              id={versionToggleId}
              checked={showEarlierWorkspaceVersions}
              onCheckedChange={setShowEarlierWorkspaceVersions}
            />
            <label
              htmlFor={versionToggleId}
              className="cursor-pointer text-xs text-muted-foreground leading-snug"
            >
              Vis tidligere versioner
            </label>
          </div>
        ) : null}
        {displayRows.map(({ link, linkId, anchorId }, index) => {
          const key = `${anchorId}-${index}`;
          const rowLabel = getOtherSideReferenceLabel(link, currentDocumentId);

          const isWorkspaceTooltip =
            Boolean(currentDocumentId) &&
            isOtherSideWorkspaceLink(link, currentDocumentId);
          const tooltipText = isWorkspaceTooltip
            ? getOtherSideWorkspaceName(link, currentDocumentId)
            : currentDocumentId
              ? getOtherSideLegislationTitleForTooltip(link, currentDocumentId)
              : undefined;

          if (!tooltipText) {
            return (
              <DropdownMenuItem
                key={key}
                onClick={(e) => {
                  handleItemClick(linkId, e);
                }}
                onPointerEnter={() => {
                  handleSidebarItemPointerEnter(linkId);
                  handleItemPointerEnter(link);
                }}
                onPointerLeave={handleItemPointerLeave}
              >
                {rowLabel}
              </DropdownMenuItem>
            );
          }

          return (
            <Tooltip key={key}>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  onClick={(e) => {
                    handleItemClick(linkId, e);
                  }}
                  onPointerEnter={() => {
                    handleSidebarItemPointerEnter(linkId);
                    handleItemPointerEnter(link);
                  }}
                  onPointerLeave={handleItemPointerLeave}
                >
                  {rowLabel}
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                className="max-w-xs"
                onPointerDown={(e) => e.stopPropagation()}
              >
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-background/75">
                    {isWorkspaceTooltip ? "Workspace" : "Lovtitel"}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-xs">{tooltipText}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
